(ns hai.library.connections.setup
  "\"Set up Humble where you work\" strip on the Connections page.

  Nudges new users to install the Chrome extension and the Slack app. We
  can't detect installs, so the user marks each step as done manually; once
  both are marked, the strip collapses to a slim confirmation row with a
  Manage link that reopens it.

  Completed steps live under :settings/local, persisted to localStorage via
  the settings-local interceptor (same mechanism as dark mode). That makes
  the tick per-browser rather than per-user — acceptable for a nudge, and
  for the Chrome card per-browser is the more accurate scope anyway."
  #?(:cljs (:require-macros hai.library.connections.setup))
  #?(:cljs
     (:require
      [hai.ui.components.new-design.utils :refer [px->rem]]
      [hai.ui.icons :as icons]
      [hai.ui.rf-sugar :refer [<s >e defevent defsub]]
      [hai.ui.tokens :as t]
      [hai.web-app.util.rf-storage :as rf-storage]
      [lambdaisland.ornament :as o]
      [url.install :as install])
     :clj
     (:require
      [hai.ui.components.new-design.utils :refer [px->rem]]
      [hai.ui.icons :as icons]
      [hai.ui.rf-sugar :refer [<s >e defevent defsub]]
      [hai.ui.tokens :as t]
      [lambdaisland.ornament :as o]
      [url.install :as install])))

(def step-keys
  [:setup.step/chrome :setup.step/slack])

(defn- update-steps [db f step]
  (update-in db [:settings/local ::completed-steps]
             (fn [steps] (f (set steps) step))))

;; ---------------------------------------------------------------------------
;; Events

#?(:cljs
   (defevent ::mark-done <-> [(rf-storage/settings-local)]
     [db step]
     ;; Leaving manage mode here lets the strip collapse as soon as the last
     ;; step is re-ticked.
     (-> db
         (update-steps conj step)
         (dissoc ::managing?))))

#?(:cljs
   (defevent ::unmark <-> [(rf-storage/settings-local)]
     [db step]
     (update-steps db disj step)))

(defevent ::manage
  ;; Reopens the full strip even when all steps are done. Ephemeral on
  ;; purpose — not persisted.
  [db]
  (assoc db ::managing? true))

;; ---------------------------------------------------------------------------
;; Subscriptions

(defsub ::completed-steps [db]
  (set (get-in db [:settings/local ::completed-steps])))

(defsub ::step-done?
  [step
   steps :<- ::completed-steps]
  (contains? steps step))

(defsub ::all-done?
  [steps :<- ::completed-steps]
  (every? steps step-keys))

(defsub ::managing? [db]
  (boolean (::managing? db)))

(defsub ::collapsed?
  ;; Show the slim row only when everything's done AND we're not in manage
  ;; mode.
  [all-done? :<- ::all-done?
   managing? :<- ::managing?]
  (and all-done? (not managing?)))

;; ---------------------------------------------------------------------------
;; Views

(def setup-steps
  [{:step  :setup.step/chrome
    :icon  icons/chrome
    :title "Chrome extension"
    :body  (str "Stop losing the tab — and the thought. Turn any page into a "
                "task or note, with the source attached. Link an AI "
                "conversation, an article or a doc to what you're working on, "
                "so the context is still there when you come back.")
    :cta   "Add to Chrome"
    :url   install/chrome-store-url}
   #_{:step  :setup.step/slack
      :icon  icons/slack
      :title "Slack app"
      :body  (str "Send anything to @Humble — a message, a link, even a photo "
                  "of your handwritten notes — and it lands on your private "
                  "timeline, just between you and Humble.")
      :cta   "Add to Slack"
      :url   "https://platform.thehumbleai.com/chat/slack/v1/A05KNFGBWGL/install"}])

(o/defstyled strip-section :section
  {:margin-bottom (px->rem 40)}
  [:h2 {:font-size (px->rem 24)
        :font-weight "700"
        :color t/--colour-ink
        :margin (str "0 0 " (px->rem 8))}])

(o/defstyled strip-lead :p
  {:color t/--text-secondary-80
   :font-size (px->rem 16)
   :line-height "1.5"
   :margin (str "0 0 " (px->rem 16))})

(o/defstyled strip-cards :div
  {:display "grid"
   :grid-template-columns "repeat(auto-fit, minmax(20rem, 1fr))"
   :gap (px->rem 16)})

(o/defstyled strip-card :div
  {:background-color t/--card-cream
   :border-radius (px->rem 8)
   :padding (px->rem 24)}
  [:&.done {:background-color t/--colour-mint-20}])

(o/defstyled card-top :div
  :flex :items-center
  {:gap (px->rem 8)
   :margin-bottom (px->rem 8)}
  [:h3 {:font-size (px->rem 18)
        :font-weight "600"
        :color t/--text-secondary
        :margin "0"}])

(o/defstyled icon-circle :div
  :flex :items-center :justify-center
  {:width (px->rem 40)
   :height (px->rem 40)
   :border-radius "50%"
   :background-color t/--colour-white
   :flex-shrink "0"}
  [:svg {:max-width (px->rem 24)
         :max-height (px->rem 24)}])

(o/defstyled card-body :p
  {:color t/--text-secondary
   :font-size (px->rem 16)
   :line-height "1.5"
   :max-width "65ch"
   :margin (str "0 0 " (px->rem 16))})

(o/defstyled card-actions :div
  :flex :items-center
  {:gap (px->rem 16)})

(o/defstyled install-link :a
  ;; The page's one major-CTA moment, so Purple — "Add connection" stays Ink.
  {:display "inline-flex"
   :align-items "center"
   :background-color t/--colour-purple
   :color t/--colour-white
   :font-size (px->rem 16)
   :font-weight "600"
   :padding (str (px->rem 8) " " (px->rem 16))
   :border-radius (px->rem 8)
   :text-decoration "none"
   :transition "background-color 0.2s"}
  [:&:hover {:background-color t/--colour-ink}]
  [:&:focus-visible {:outline (str "2px solid " t/--colour-purple)
                     :outline-offset "2px"}])

(o/defstyled quiet-button :button
  {:background "none"
   :border "none"
   :padding "0"
   :color t/--colour-purple
   :font-size (px->rem 14)
   :font-weight "600"
   :font-family "inherit"
   :cursor "pointer"}
  [:&:hover {:text-decoration "underline"}]
  [:&:focus-visible {:outline (str "2px solid " t/--colour-purple)
                     :outline-offset "2px"}])

(o/defstyled done-tag :span
  {:color t/--colour-mint-ink
   :font-size (px->rem 14)
   :font-weight "600"})

(o/defstyled done-row :div
  :flex :items-center :justify-between
  {:background-color t/--colour-mint-20
   :border-radius (px->rem 8)
   :padding (str (px->rem 16) " " (px->rem 24))
   :margin-bottom (px->rem 40)}
  [:.label {:color t/--colour-mint-ink
            :font-size (px->rem 15)
            :font-weight "600"}])

(defn setup-card [{:keys [step icon title body cta url]}]
  (let [done? @(<s [::step-done? step])]
    [strip-card {:class (when done? "done")}
     [card-top
      [icon-circle [icon {:size 24}]]
      [:h3 title]]
     [card-body body]
     (if done?
       [card-actions
        [done-tag "✓ Marked as done"]
        [quiet-button {:on-click #(>e [::unmark step])} "Undo"]]
       [card-actions
        [install-link {:href url :target "_blank" :rel "noopener"} cta]
        [quiet-button {:on-click #(>e [::mark-done step])} "Mark as done"]])]))

(defn where-you-work
  "The setup strip: two install cards, or — once both are marked done — a
  slim confirmation row with a Manage link that reopens them."
  []
  (if @(<s [::collapsed?])
    [done-row
     [:span.label "✓ Humble is set up in Chrome"]
     [quiet-button {:on-click #(>e [::manage])} "Manage"]]
    [strip-section
     [:h2 "Set up Humble where you work"]
     [strip-lead
      (str "One quick install puts Humble inside your browser, so "
           "you can capture wherever you already work.")]
     [strip-cards
      (for [step setup-steps]
        ^{:key (:step step)} [setup-card step])]]))
